import { useContext, useState } from 'react'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import FormHelperText from '@mui/material/FormHelperText'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Icon from 'src/@core/components/icon'
import PropTypes from 'prop-types'
import { AuthContext } from 'src/context/AuthContext'
import { getImageSrc } from 'src/utils/imageSrc'

const ClientAvatar = ({ image, name }) => {
  const [imgError, setImgError] = useState(false)
  const firstLetter = name?.charAt(0)?.toUpperCase() || '?'

  if (!image || imgError) {
    return (
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          background: 'linear-gradient(140deg, #3d82d4 0%, #63a5ef 100%)',
          border: '1.5px solid rgba(61,130,212,0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: '#fff',
          fontWeight: 700,
          fontSize: 15,
          fontFamily: 'inherit',
          userSelect: 'none'
        }}
      >
        {firstLetter}
      </Box>
    )
  }

  return (
    <img
      src={getImageSrc(image)}
      alt={name}
      onError={() => setImgError(true)}
      style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
    />
  )
}

const LoginClientList = props => {
  const {
    openClientList,
    handlecloseClientList,
    clientData,
    selectedClientId,
    setSelectedClientId,
    handleLoginClient
  } = props

  const { setLoggedInClientId, isUserLoggedIn } = useContext(AuthContext)

  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSelection = async clientId => {
    setSelectedClientId(clientId)
    setError('')
    if (isUserLoggedIn) {
      const selectedClient = clientData?.clients?.find(c => c.clientId === clientId)
      await handleLoginClient({ clientId: selectedClient?.clientId, email: clientData?.email, is_favourite: true })
      setLoggedInClientId(selectedClient.clientId)
      handlecloseClientList()
    }
  }

  const handleClientLogin = async () => {
    if (selectedClientId) {
      const selectedClient = clientData?.clients?.find(c => c.clientId === selectedClientId)
      setLoading(true)
      await handleLoginClient({ clientId: selectedClient?.clientId, email: clientData?.email, is_favourite: true })
      setLoggedInClientId(selectedClient?.clientId)
      handlecloseClientList()
      setLoading(false)
    } else {
      setError('Please select a customer to continue.')
    }
  }

  const clientCount = clientData?.clients?.length ?? 0

  return (
    <Dialog
      disableRestoreFocus
      fullWidth
      maxWidth='sm'
      scroll='body'
      onClose={handlecloseClientList}
      open={openClientList}
      PaperProps={{ sx: { borderRadius: '12px', maxHeight: '82vh', overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: '#1B205C',
          px: 4,
          py: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              backgroundColor: 'rgba(255,255,255,0.12)',
              p: 1.25,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon icon='mdi:account-multiple' style={{ color: 'white', fontSize: '22px' }} />
          </Box>
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.35 }}>
              Select Customer
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.58)', fontSize: '0.775rem', lineHeight: 1.4 }}>
              Choose an account to continue
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handlecloseClientList}
          size='small'
          sx={{ color: 'rgba(255,255,255,0.65)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' } }}
        >
          <Icon icon='mdi:close' />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent sx={{ px: 3.5, pt: 3, pb: 2, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'text.disabled'
            }}
          >
            {clientCount} {clientCount === 1 ? 'account' : 'accounts'} available
          </Typography>
        </Box>

        {error && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
            <Icon icon='mdi:alert-circle-outline' style={{ color: '#d32f2f', fontSize: '16px' }} />
            <FormHelperText sx={{ color: 'error.main', m: 0 }}>{error}</FormHelperText>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {clientData?.clients?.map(client => {
            const isSelected = selectedClientId === client.clientId

            return (
              <Box
                key={client.clientId}
                onClick={() => handleSelection(client.clientId)}
                sx={{
                  border: `1.5px solid ${isSelected ? '#1B205C' : '#E5E7EB'}`,
                  borderRadius: '10px',
                  px: 2.5,
                  py: 1.75,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  backgroundColor: isSelected ? 'rgba(27,32,92,0.05)' : '#FAFAFA',
                  cursor: 'pointer',
                  transition: 'all 0.18s ease',
                  boxShadow: isSelected ? '0 0 0 3px rgba(27,32,92,0.07)' : 'none',
                  '&:hover': {
                    borderColor: '#c5d4e8',
                    backgroundColor: '#f4f7fd',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 14px rgba(27,32,92,0.09)'
                  }
                }}
              >
                {/* Logo */}
                <ClientAvatar image={client.image} name={client.clientName} />

                {/* Name */}
                <Box flex={1} sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: isSelected ? '#1B205C' : '#1a1a2e',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {client.clientName}
                  </Typography>
                </Box>

                {/* Selection indicator */}
                {isSelected ? (
                  <CheckCircleIcon sx={{ color: '#1B205C', fontSize: 21, flexShrink: 0 }} />
                ) : (
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      border: '2px solid #D1D5DB',
                      flexShrink: 0
                    }}
                  />
                )}
              </Box>
            )
          })}
        </Box>
      </DialogContent>

      {!isUserLoggedIn && (
        <>
          <Divider />
          <DialogActions sx={{ px: 3.5, py: 2.5, gap: 1.5, justifyContent: 'flex-end' }}>
            <Button
              variant='outlined'
              color='secondary'
              onClick={handlecloseClientList}
              sx={{ minWidth: 88, fontWeight: 500 }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={handleClientLogin}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={15} color='inherit' /> : null}
              sx={{
                minWidth: 110,
                fontWeight: 600,
                backgroundColor: '#1B205C',
                '&:hover': { backgroundColor: '#14184a' }
              }}
            >
              {loading ? 'Logging in…' : 'Login'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

LoginClientList.propTypes = {
  openClientList: PropTypes.bool,
  handlecloseClientList: PropTypes.func.isRequired,
  clientData: PropTypes.shape({
    clients: PropTypes.arrayOf(
      PropTypes.shape({
        clientId: PropTypes.string.isRequired,
        clientName: PropTypes.string.isRequired,
        image: PropTypes.string
      })
    ),
    email: PropTypes.string
  }).isRequired,
  selectedClientId: PropTypes.string,
  setSelectedClientId: PropTypes.func.isRequired,
  handleLoginClient: PropTypes.func.isRequired,
  tempAuthToken: PropTypes.string
}

export default LoginClientList
